import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import { downloadCsv } from "@/lib/csv";
import { logExportTask } from "@/lib/exportTaskLog";
import { computeWebhookHealth, downloadWebhookHealthSnapshot, type HealthWindow } from "@/lib/webhookHealth";
import type { WebhookDelivery, WebhookEndpoint, WebhookEndpointEvent } from "@/pages/admin/approvals/hooks/useWebhooks";

interface WebhookPanelProps {
  endpoints: WebhookEndpoint[];
  deliveries: WebhookDelivery[];
  events: WebhookEndpointEvent[];
  loading: boolean;
  onCreate: () => void;
  onEdit: (endpoint: WebhookEndpoint) => void;
  onToggleStatus: (endpoint: WebhookEndpoint) => void;
  onProbeResume: (endpoint: WebhookEndpoint) => void | Promise<void>;
  onMarkFixed: (endpoint: WebhookEndpoint) => void | Promise<void>;
  onUndoMarkFixed: (endpoint: WebhookEndpoint) => void | Promise<void>;
  onDelete: (endpoint: WebhookEndpoint) => void;
  onTest: (endpoint: WebhookEndpoint) => void;
  onRetry: (delivery: WebhookDelivery) => void;
  onProcessRetries: () => void | Promise<void>;
}

const DELIVERY_TONE: Record<string, string> = {
  success: "bg-primary-100 text-primary-700",
  failed: "bg-accent-100 text-accent-800",
  retrying: "bg-secondary-100 text-secondary-900",
  pending: "bg-secondary-100 text-secondary-900",
};

// 端点健康事件的样式与图标映射。
const EVENT_TONE: Record<string, string> = {
  auto_paused: "bg-accent-100 text-accent-700",
  manual_pause: "bg-background-200 text-foreground-600",
  manual_resume: "bg-primary-100 text-primary-700",
  probe_passed: "bg-primary-100 text-primary-700",
  probe_failed: "bg-accent-100 text-accent-700",
  resume_replay: "bg-secondary-100 text-secondary-900",
  replay_degraded: "bg-accent-100 text-accent-800",
  marked_fixed: "bg-primary-100 text-primary-700",
  undid_fixed: "bg-accent-100 text-accent-700",
  failures_reset: "bg-secondary-100 text-secondary-900",
};

const EVENT_ICON: Record<string, string> = {
  auto_paused: "ri-stop-circle-line",
  manual_pause: "ri-pause-line",
  manual_resume: "ri-play-line",
  probe_passed: "ri-stethoscope-line",
  probe_failed: "ri-close-circle-line",
  resume_replay: "ri-restart-line",
  replay_degraded: "ri-alert-line",
  marked_fixed: "ri-checkbox-circle-line",
  undid_fixed: "ri-arrow-go-back-line",
  failures_reset: "ri-refresh-line",
};

// 把未来的重试时间格式化成倒计时（如 45s / 2m / 8m）。
function formatCountdown(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "0s";
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  return `${Math.floor(min / 60)}h`;
}

// 事件回调面板：端点订阅管理 + 投递日志（含状态 / HTTP / 响应体 / 重试）。
export default function WebhookPanel({
  endpoints,
  deliveries,
  events,
  loading,
  onCreate,
  onEdit,
  onToggleStatus,
  onProbeResume,
  onMarkFixed,
  onUndoMarkFixed,
  onDelete,
  onTest,
  onRetry,
  onProcessRetries,
}: WebhookPanelProps) {
  const { t } = useTranslation();
  const [endpointFilter, setEndpointFilter] = useState<number | "all">("all");
  const [healthWindow, setHealthWindow] = useState<HealthWindow>("7d");
  const [eventEndpointFilter, setEventEndpointFilter] = useState<number | "all">("all");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("all");
  const [probingId, setProbingId] = useState<number | null>(null);

  // 按所选时间窗口过滤投递，以便只看近期表现。
  const windowedDeliveries = useMemo(() => {
    const ms = healthWindow === "24h" ? 86400000 : healthWindow === "7d" ? 604800000 : 2592000000;
    const cutoff = Date.now() - ms;
    return deliveries.filter((item) => new Date(item.created_at).getTime() >= cutoff);
  }, [deliveries, healthWindow]);

  const healthWindowOptions: { key: HealthWindow; label: string }[] = [
    { key: "24h", label: t("approvals.healthWindow24h") },
    { key: "7d", label: t("approvals.healthWindow7d") },
    { key: "30d", label: t("approvals.healthWindow30d") },
  ];

  const endpointName = useMemo(() => {
    const map = new Map<number, string>();
    endpoints.forEach((endpoint) => map.set(endpoint.id, endpoint.name));
    return map;
  }, [endpoints]);

  // 健康事件时间线的筛选：按端点与事件类型缩小范围，端点多了之后能只看某个接收端的停复历史。
  const eventTypeOptions = useMemo(() => {
    const set = new Set<string>();
    events.forEach((event) => set.add(event.event_type));
    return Array.from(set);
  }, [events]);

  const filteredEvents = useMemo(
    () =>
      events.filter((event) => {
        if (eventEndpointFilter !== "all" && event.endpoint_id !== eventEndpointFilter) return false;
        if (eventTypeFilter !== "all" && event.event_type !== eventTypeFilter) return false;
        return true;
      }),
    [events, eventEndpointFilter, eventTypeFilter],
  );

  // 每个端点的投递成功率统计（基于已加载的投递记录）。
  const statsByEndpoint = useMemo(() => {
    const map = new Map<number, { total: number; success: number; failed: number; retrying: number }>();
    deliveries.forEach((delivery) => {
      if (delivery.endpoint_id == null) return;
      const entry = map.get(delivery.endpoint_id) ?? { total: 0, success: 0, failed: 0, retrying: 0 };
      entry.total += 1;
      if (delivery.status === "success") entry.success += 1;
      else if (delivery.status === "failed") entry.failed += 1;
      else if (delivery.status === "retrying") entry.retrying += 1;
      map.set(delivery.endpoint_id, entry);
    });
    return map;
  }, [deliveries]);

  const pendingRetryCount = useMemo(
    () => deliveries.filter((item) => item.status === "retrying").length,
    [deliveries],
  );

  // 端点健康总览：汇总每个接收端在所选时间窗口内的成功率、平均响应耗时与最近一次投递，
  // 按稳定性排序（成功率越低越靠前），方便一眼找出最不稳定的接收端。
  const health = useMemo(
    () => computeWebhookHealth(endpoints, deliveries, healthWindow),
    [endpoints, deliveries, healthWindow],
  );

  const leastStableId =
    health.length > 0 && health[0].rate != null && health[0].rate < 100 ? health[0].endpointId : null;

  const rateBarClass = (rate: number | null) =>
    rate == null ? "bg-background-300" : rate >= 99 ? "bg-primary-500" : rate >= 90 ? "bg-secondary-500" : "bg-accent-500";

  const filtered = useMemo(
    () => (endpointFilter === "all" ? deliveries : deliveries.filter((item) => item.endpoint_id === endpointFilter)),
    [deliveries, endpointFilter],
  );

  const statusLabel = (status: string) =>
    status === "success"
      ? t("approvals.deliverySuccess")
      : status === "failed"
        ? t("approvals.deliveryFailed")
        : status === "retrying"
          ? t("approvals.deliveryRetrying")
          : t("approvals.deliveryPending");

  const eventLabel = (type: string) =>
    type === "auto_paused"
      ? t("approvals.eventAutoPaused")
      : type === "manual_resume"
        ? t("approvals.eventManualResume")
        : type === "manual_pause"
          ? t("approvals.eventManualPause")
          : type === "failures_reset"
            ? t("approvals.eventFailuresReset")
            : type === "probe_passed"
              ? t("approvals.eventProbePassed")
              : type === "probe_failed"
                ? t("approvals.eventProbeFailed")
                : type === "resume_replay"
                  ? t("approvals.eventResumeReplay")
                  : type === "replay_degraded"
                    ? t("approvals.eventReplayDegraded")
                    : type === "marked_fixed"
                      ? t("approvals.eventMarkedFixed")
                      : type === "undid_fixed"
                        ? t("approvals.eventUndidFixed")
                        : type;

  // 把当前时间窗口下的端点健康总览导出为 CSV，方便做可靠性周报。
  const handleExportHealth = () => {
    if (health.length === 0) return;
    const windowLabel = healthWindowOptions.find((option) => option.key === healthWindow)?.label ?? healthWindow;
    downloadWebhookHealthSnapshot(endpoints, deliveries, healthWindow, {
      endpoint: t("approvals.healthColEndpoint"),
      rate: t("approvals.healthColRate"),
      latency: t("approvals.healthColLatency"),
      total: t("approvals.healthColTotal"),
      failed: t("approvals.healthColFailed"),
      last: t("approvals.healthColLast"),
      lastTime: t("approvals.colDeliveryTime"),
      fileName: t("approvals.exportHealthFileName"),
      statusLabel,
    });
    // 本次导出同步写入报表中心的导出任务历史（记录导出人 / 时间 / 行数），让分散导出也统一留痕。
    void logExportTask({
      reportKey: "webhook-health",
      reportName: `${t("approvals.exportHealthFileName")} · ${windowLabel}`,
      category: "监控",
      rowCount: health.length,
      windowLabel,
    });
  };

  // 把当前筛选出的端点健康事件导出为 CSV，方便把某个接收端的整段停复历史存档。
  const handleExportEvents = () => {
    if (filteredEvents.length === 0) return;
    downloadCsv(
      `${t("approvals.exportEventsFileName")}.csv`,
      [
        { key: "time", label: t("approvals.eventColTime"), value: (row) => formatFullDate(row.created_at) },
        { key: "type", label: t("approvals.eventColType"), value: (row) => eventLabel(row.event_type) },
        { key: "endpoint", label: t("approvals.eventColEndpoint"), value: (row) => row.endpoint_name ?? "" },
        { key: "detail", label: t("approvals.eventColDetail"), value: (row) => row.detail ?? "" },
        { key: "actor", label: t("approvals.eventColActor"), value: (row) => row.actor_name ?? "" },
      ],
      filteredEvents,
    );
    void logExportTask({
      reportKey: "webhook-events",
      reportName: t("approvals.exportEventsFileName"),
      category: "监控",
      rowCount: filteredEvents.length,
    });
  };

  const handleProbe = async (endpoint: WebhookEndpoint) => {
    setProbingId(endpoint.id);
    try {
      await onProbeResume(endpoint);
    } finally {
      setProbingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* 端点健康总览 */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-heart-pulse-line" />
            </span>
            <div>
              <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("approvals.healthTitle")}</h2>
              <p className="text-[11px] text-foreground-500">{t("approvals.healthSub")}</p>
            </div>
          </div>
          <div className="flex flex-col items-start gap-1.5 sm:items-end">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
                {healthWindowOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setHealthWindow(option.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                      healthWindow === option.key
                        ? "bg-background-50 text-foreground-950"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleExportHealth}
                disabled={loading || health.length === 0}
                title={t("approvals.exportHealthTitle")}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
                  <i className="ri-download-2-line" />
                </span>
                {t("approvals.exportHealth")}
              </button>
            </div>
            <p className="flex items-center gap-1 text-[10px] text-foreground-400">
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-filter-3-line" />
              </span>
              {t("approvals.healthWindowHint")}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="mt-4 flex flex-col gap-2">
            {[0, 1].map((item) => (
              <div key={item} className="h-10 rounded-md bg-background-100" />
            ))}
          </div>
        ) : health.length === 0 ? (
          <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-8 text-center text-xs text-foreground-500">
            {t("approvals.healthEmpty")}
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[780px] text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-[11px] font-medium text-foreground-600">
                  <th className="px-3 py-3">{t("approvals.healthColEndpoint")}</th>
                  <th className="px-3 py-3">{t("approvals.healthColRate")}</th>
                  <th className="px-3 py-3 text-right">{t("approvals.healthColLatency")}</th>
                  <th className="px-3 py-3 text-right">{t("approvals.healthColTotal")}</th>
                  <th className="px-3 py-3 text-right">{t("approvals.healthColFailed")}</th>
                  <th className="px-3 py-3">{t("approvals.healthColLast")}</th>
                </tr>
              </thead>
              <tbody>
                {health.map((row) => (
                  <tr
                    key={row.endpointId}
                    className={`border-t border-background-200 ${
                      row.endpointId === leastStableId ? "bg-accent-50/60" : ""
                    }`}
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-foreground-950">{row.endpointName}</span>
                        {row.endpointId === leastStableId && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                            <i className="ri-alert-line" />
                            {t("approvals.healthLeastStable")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-10 shrink-0 font-heading text-xs font-semibold text-foreground-950">
                          {row.rate == null ? "—" : `${row.rate}%`}
                        </span>
                        <span className="h-1.5 w-24 overflow-hidden rounded-full bg-background-200">
                          <span
                            className={`block h-full rounded-full ${rateBarClass(row.rate)}`}
                            style={{ width: `${row.rate ?? 0}%` }}
                          />
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-foreground-800">
                      {row.avg == null ? "—" : t("approvals.healthAvgUnit", { ms: row.avg })}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-foreground-800">{row.total}</td>
                    <td className="px-3 py-2.5 text-right text-xs">
                      {row.failed > 0 ? (
                        <span className="inline-flex items-center gap-1 text-accent-700">
                          <i className="ri-error-warning-line" />
                          {row.failed}
                        </span>
                      ) : (
                        <span className="text-foreground-400">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.lastStatus && row.lastTime ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              DELIVERY_TONE[row.lastStatus] ?? "bg-background-200 text-foreground-600"
                            }`}
                          >
                            {statusLabel(row.lastStatus)}
                          </span>
                          <span className="text-[11px] text-foreground-500">
                            {formatRelativeTime(row.lastTime)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-foreground-400">{t("approvals.healthNoDelivery")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 端点列表 */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-webhook-line" />
            </span>
            <div>
              <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("approvals.endpointsTitle")}</h2>
              <p className="text-[11px] text-foreground-500">{t("approvals.endpointsSub")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCreate}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-add-line" />
            </span>
            {t("approvals.newEndpoint")}
          </button>
        </div>

        <p className="mt-3 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-[11px] text-accent-800">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-shield-cross-line" />
          </span>
          {t("approvals.autoDegradeHint", { threshold: 5 })}
        </p>
        <p className="mt-2 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-[11px] text-primary-800">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-stethoscope-line" />
          </span>
          {t("approvals.recoverPrecheckHint")}
        </p>

        {loading ? (
          <div className="mt-4 flex flex-col gap-2">
            {[0, 1].map((item) => (
              <div key={item} className="h-16 rounded-md bg-background-100" />
            ))}
          </div>
        ) : endpoints.length === 0 ? (
          <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-8 text-center text-xs text-foreground-500">
            {t("approvals.endpointsEmpty")}
          </p>
        ) : (
          <ul className="mt-4 flex flex-col gap-3">
            {endpoints.map((endpoint) => (
              <li key={endpoint.id} className="rounded-md border border-background-200 bg-background-100 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-foreground-950">{endpoint.name}</p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          endpoint.status === "active" ? "bg-primary-100 text-primary-700" : "bg-background-200 text-foreground-500"
                        }`}
                      >
                        {endpoint.status === "active" ? t("approvals.endpointEnabled") : t("approvals.endpointPaused")}
                      </span>
                      {endpoint.secret && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-900">
                          <i className="ri-lock-2-line" />
                          {t("approvals.signedTag")}
                        </span>
                      )}
                      {endpoint.auto_paused_at && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                          <i className="ri-stop-circle-line" />
                          {t("approvals.endpointAutoPaused")}
                        </span>
                      )}
                      {endpoint.needs_attention && (
                        <span
                          title={t("approvals.endpointNeedsAttentionHint")}
                          className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800"
                        >
                          <i className="ri-alert-line" />
                          {t("approvals.endpointNeedsAttention")}
                        </span>
                      )}
                      {!endpoint.auto_paused_at && Number(endpoint.consecutive_failures ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-accent-50 px-2 py-0.5 text-[10px] font-medium text-accent-700">
                          <i className="ri-error-warning-line" />
                          {t("approvals.endpointConsecutive", { count: Number(endpoint.consecutive_failures ?? 0) })}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 truncate font-mono text-[11px] text-foreground-500">{endpoint.url}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {endpoint.events.map((event) => (
                        <span key={event} className="rounded-full border border-background-300 bg-background-50 px-2 py-0.5 font-mono text-[10px] text-foreground-600">
                          {event}
                        </span>
                      ))}
                    </div>
                    {(() => {
                      const stat = statsByEndpoint.get(endpoint.id);
                      const total = stat?.total ?? 0;
                      const success = stat?.success ?? 0;
                      const failed = stat?.failed ?? 0;
                      const rate = total > 0 ? Math.round((success / total) * 100) : null;
                      return (
                        <div className="mt-3 rounded-md border border-background-200 bg-background-50 px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground-700">
                              <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-600">
                                <i className="ri-line-chart-line" />
                              </span>
                              {t("approvals.endpointSuccessRate")}
                            </span>
                            <span className="flex items-center gap-2 text-[11px]">
                              <span className="font-heading text-sm font-semibold text-foreground-950">
                                {rate == null ? "—" : `${rate}%`}
                              </span>
                              <span className="text-foreground-500">{t("approvals.endpointStatTotal", { count: total })}</span>
                              {failed > 0 && (
                                <span className="text-accent-700">
                                  <i className="ri-error-warning-line align-middle" /> {failed}
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                            <div
                              className="h-full rounded-full bg-primary-500"
                              style={{ width: `${rate ?? 0}%` }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => onTest(endpoint)}
                      className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <i className="ri-send-plane-line" />
                      {t("approvals.test")}
                    </button>
                    {endpoint.status === "active" ? (
                      <button
                        type="button"
                        onClick={() => onToggleStatus(endpoint)}
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                      >
                        <i className="ri-pause-line" />
                        {t("approvals.pause")}
                      </button>
                    ) : endpoint.auto_paused_at ? (
                      <button
                        type="button"
                        disabled={probingId === endpoint.id}
                        onClick={() => void handleProbe(endpoint)}
                        title={t("approvals.recoverPrecheckTitle")}
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <i className={probingId === endpoint.id ? "ri-loader-4-line animate-spin" : "ri-stethoscope-line"} />
                        {probingId === endpoint.id ? t("approvals.recoverPrecheckRunning") : t("approvals.recoverPrecheck")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onToggleStatus(endpoint)}
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                      >
                        <i className="ri-play-line" />
                        {t("approvals.resume")}
                      </button>
                    )}
                    {endpoint.needs_attention && (
                      <button
                        type="button"
                        onClick={() => void onMarkFixed(endpoint)}
                        title={t("approvals.markFixedTitle")}
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
                      >
                        <i className="ri-checkbox-circle-line" />
                        {t("approvals.markFixed")}
                      </button>
                    )}
                    {endpoint.fix_snapshot && (
                      <button
                        type="button"
                        onClick={() => void onUndoMarkFixed(endpoint)}
                        title={t("approvals.undoMarkFixedTitle")}
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
                      >
                        <i className="ri-arrow-go-back-line" />
                        {t("approvals.undoMarkFixed")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onEdit(endpoint)}
                      className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <i className="ri-edit-line" />
                      {t("approvals.edit")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(endpoint)}
                      className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
                    >
                      <i className="ri-delete-bin-line" />
                      {t("approvals.delete")}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 健康事件时间线 */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-history-line" />
            </span>
            <div>
              <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("approvals.healthEventTitle")}</h2>
              <p className="text-[11px] text-foreground-500">{t("approvals.healthEventSub")}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] text-foreground-500">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-400">
                <i className="ri-webhook-line" />
              </span>
              <select
                value={eventEndpointFilter}
                onChange={(event) => setEventEndpointFilter(event.target.value === "all" ? "all" : Number(event.target.value))}
                className="h-8 min-w-[130px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-800 outline-none transition-colors hover:bg-background-100 focus:border-primary-400"
              >
                <option value="all">{t("approvals.healthEventAllEndpoints")}</option>
                {endpoints.map((endpoint) => (
                  <option key={endpoint.id} value={endpoint.id}>
                    {endpoint.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-foreground-500">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-400">
                <i className="ri-price-tag-3-line" />
              </span>
              <select
                value={eventTypeFilter}
                onChange={(event) => setEventTypeFilter(event.target.value)}
                className="h-8 min-w-[130px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-800 outline-none transition-colors hover:bg-background-100 focus:border-primary-400"
              >
                <option value="all">{t("approvals.healthEventAllTypes")}</option>
                {eventTypeOptions.map((type) => (
                  <option key={type} value={type}>
                    {eventLabel(type)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={handleExportEvents}
              disabled={loading || filteredEvents.length === 0}
              title={t("approvals.exportEventsTitle")}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
                <i className="ri-download-2-line" />
              </span>
              {t("approvals.exportEvents")}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="mt-4 flex flex-col gap-2">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-10 rounded-md bg-background-100" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-8 text-center text-xs text-foreground-500">
            {t("approvals.healthEventEmpty")}
          </p>
        ) : filteredEvents.length === 0 ? (
          <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-8 text-center text-xs text-foreground-500">
            {t("approvals.healthEventNoMatch")}
          </p>
        ) : (
          <ol className="mt-5 flex flex-col">
            {filteredEvents.map((event, index) => (
              <li key={event.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm ${
                      EVENT_TONE[event.event_type] ?? "bg-background-200 text-foreground-600"
                    }`}
                  >
                    <i className={EVENT_ICON[event.event_type] ?? "ri-information-line"} />
                  </span>
                  {index < filteredEvents.length - 1 && <span className="my-1 w-px flex-1 bg-background-200" />}
                </div>
                <div className={index < filteredEvents.length - 1 ? "pb-5" : "pb-1"}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-foreground-950">{eventLabel(event.event_type)}</span>
                    <span className="rounded-full border border-background-300 bg-background-50 px-1.5 py-0.5 text-[10px] text-foreground-600">
                      {event.endpoint_name ?? "—"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-foreground-600">{event.detail}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-foreground-400">
                    <span>{formatFullDate(event.created_at)}</span>
                    <span>{formatRelativeTime(event.created_at)}</span>
                    {event.actor_name && (
                      <span className="flex items-center gap-0.5">
                        <i className="ri-user-line" />
                        {event.actor_name}
                      </span>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* 投递日志 */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
              <i className="ri-list-check-2" />
            </span>
            <div>
              <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("approvals.deliveriesTitle")}</h2>
              <p className="text-[11px] text-foreground-500">{t("approvals.deliveriesSub")}</p>
            </div>
          </div>
          <select
            value={endpointFilter}
            onChange={(event) => setEndpointFilter(event.target.value === "all" ? "all" : Number(event.target.value))}
            className="h-9 min-w-[150px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
          >
            <option value="all">{t("approvals.deliveryAllEndpoints")}</option>
            {endpoints.map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2">
          <p className="flex items-center gap-2 text-[11px] text-secondary-900">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-loop-right-line" />
            </span>
            {t("approvals.autoRetryHint")}
          </p>
          <button
            type="button"
            onClick={() => void onProcessRetries()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-secondary-700">
              <i className="ri-refresh-line" />
            </span>
            {t("approvals.processRetries")}
            {pendingRetryCount > 0 && (
              <span className="ml-0.5 rounded-full bg-secondary-500 px-1.5 py-0.5 text-[10px] font-medium text-background-50">
                {pendingRetryCount}
              </span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="mt-4 flex flex-col gap-2">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-10 rounded-md bg-background-100" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-8 text-center text-xs text-foreground-500">
            {t("approvals.deliveriesEmpty")}
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-[11px] font-medium text-foreground-600">
                  <th className="px-3 py-3">{t("approvals.colDeliveryTime")}</th>
                  <th className="px-3 py-3">{t("approvals.colEndpoint")}</th>
                  <th className="px-3 py-3">{t("approvals.colEvent")}</th>
                  <th className="px-3 py-3">{t("approvals.colDeliveryStatus")}</th>
                  <th className="px-3 py-3 text-right">{t("approvals.colAttempts")}</th>
                  <th className="px-3 py-3">{t("approvals.colResponse")}</th>
                  <th className="px-3 py-3 text-right">{t("approvals.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((delivery) => (
                  <tr key={delivery.id} className="border-t border-background-200 align-top">
                    <td className="px-3 py-2.5">
                      <p className="text-xs text-foreground-900">{formatFullDate(delivery.created_at)}</p>
                      <p className="text-[11px] text-foreground-400">{formatRelativeTime(delivery.created_at)}</p>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-foreground-800">
                      {delivery.endpoint_id != null ? endpointName.get(delivery.endpoint_id) ?? "—" : "—"}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-foreground-600">{delivery.event}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${DELIVERY_TONE[delivery.status] ?? "bg-background-200 text-foreground-600"}`}>
                        {statusLabel(delivery.status)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-[11px] text-foreground-600">
                      {delivery.attempts}/{delivery.max_attempts ?? 4}
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="text-[11px] text-foreground-700">
                        {delivery.response_status != null ? `HTTP ${delivery.response_status}` : delivery.last_error ?? "—"}
                      </p>
                      {delivery.status === "retrying" && delivery.next_retry_at && (
                        <p className="mt-0.5 flex items-center gap-1 text-[10px] text-secondary-800">
                          <i className="ri-loop-right-line" />
                          {t("approvals.retryScheduled", { time: formatCountdown(delivery.next_retry_at) })}
                        </p>
                      )}
                      {delivery.response_body && (
                        <p className="mt-0.5 max-w-[220px] truncate font-mono text-[10px] text-foreground-400" title={delivery.response_body}>
                          {delivery.response_body}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {delivery.status === "failed" || delivery.status === "retrying" ? (
                        <button
                          type="button"
                          onClick={() => onRetry(delivery)}
                          className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                        >
                          <i className="ri-refresh-line" />
                          {t("approvals.retry")}
                        </button>
                      ) : (
                        <span className="text-[11px] text-foreground-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}